import React, { RefCallback } from "react";
import { Checkbox, FormControlLabel } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CrudFieldController from "@/components/tables/fields/CrudFieldController";
import { requiredBooleanRule } from "@/components/tables/fields/fieldRules";
import {
  CREATE_SED_FOR_COMPANY_FIELD_NAME,
  DEPARTMENT_SET_FIELD_NAME,
  SUBJECT_SET_FIELD_NAME,
} from "@/components/tables/fields/fieldNameRules";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";
import styles from "@/components/tables/fields/crudField.module.css";

export interface CrudCheckboxFieldProps {
  field: Pick<CrudModalField, "id" | "header" | "required" | "errorMessage">;
  entityOfTable?: string;
  validationTargetRef?: RefCallback<HTMLElement>;
  onChange?: (checked: boolean) => void;
}

// "create SED for the whole company" excludes picking single subjects or departments at the same time
const useCreateSedForCompanyLocked = (
  entityOfTable: string | undefined,
  fieldName: string | undefined
): boolean => {
  const selectedSubjectSet = useWatch({ name: SUBJECT_SET_FIELD_NAME });
  const selectedDepartmentSet = useWatch({ name: DEPARTMENT_SET_FIELD_NAME });

  if (
    entityOfTable !== "subjectExaminationDeadline" ||
    fieldName !== CREATE_SED_FOR_COMPANY_FIELD_NAME
  ) {
    return false;
  }
  return (
    (selectedSubjectSet?.length !== 0 && selectedSubjectSet !== null) ||
    selectedDepartmentSet?.length !== 0
  );
};

const CrudCheckboxField = ({
  field,
  entityOfTable,
  validationTargetRef,
  onChange,
}: CrudCheckboxFieldProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const locked = useCreateSedForCompanyLocked(entityOfTable, field.id);

  return (
    <CrudFieldController
      name={field.id as string}
      control={control}
      defaultValue={field.required ? undefined : false}
      rules={requiredBooleanRule(field, t)}
      validationTargetRef={validationTargetRef}
      render={({
        field: { onChange: setFormValue, value, ...rest },
        error,
      }) => (
        <FormControlLabel
          label={
            field.required ? (
              <>
                {field.header}
                <span className={styles.labelRequired}>{"\u{002A}"}</span>
              </>
            ) : (
              field.header
            )
          }
          control={
            <Checkbox
              {...rest}
              checked={value === true}
              onChange={(event) => {
                onChange?.(event.target.checked);
                setFormValue(event.target.checked);
              }}
              disabled={locked}
              color={error ? "error" : "primary"}
            />
          }
        />
      )}
    />
  );
};

export default CrudCheckboxField;
